import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { THEMES } from '../config/constants';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children, theme = 'light' }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      removeToast(id);
    }, 3000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    window.alert = (message) => {
      let type = 'info';
      const lowerMsg = message.toLowerCase();
      if (lowerMsg.includes('error') || lowerMsg.includes('failed') || lowerMsg.includes('invalid')) type = 'error';
      else if (lowerMsg.includes('saved') || lowerMsg.includes('success') || lowerMsg.includes('complete') || lowerMsg.includes('imported')) type = 'success';
      addToast(message, type);
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 50, scale: 0.3 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
              className={`pointer-events-auto flex items-center gap-3 px-5 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border border-white/20 min-w-[300px] ${
                theme === 'dark' || theme === 'midnightNeon' ? 'bg-slate-900/80 text-white' : 'bg-white/80 text-slate-800'
              }`}
            >
              <div className="shrink-0">
                {toast.type === 'success' && <CheckCircle className="text-emerald-500" size={24} />}
                {toast.type === 'error' && <AlertCircle className="text-rose-500" size={24} />}
                {toast.type === 'info' && <Info className="text-blue-500" size={24} />}
              </div>
              <p className="flex-1 font-semibold text-sm">{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={18} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
